/**
 * PoliciesPageV2 — Phase 7 of the Rebirth migration.
 *
 * Round 5 upgrade: replace placeholder cards on all 8 tabs with first-pass
 * real content. Static representative data; swap to backend reads when
 * the v2 endpoints land.
 */
import React, { useState } from 'react';
import { Settings as SettingsIcon, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { isRebirthPoliciesEnabled } from '../utils/featureFlags';
import PolicyConfigPage from './PolicyConfigPage';
import { V2PageShell, Button, Pill, Card, MetricCard, Table, PostureBanner } from '../components/v2';

const POLICY_TABS = [
  { id: 'policy-skills',       label: 'Policy Skills' },
  { id: 'policy-library',      label: 'Policy Library' },
  { id: 'control-config',      label: 'Control Config' },
  { id: 'framework-coverage',  label: 'Framework Coverage' },
  { id: 'control-drift',       label: 'Control Drift' },
  { id: 'control-scorecard',   label: 'Control Scorecard' },
  { id: 'data-classification', label: 'Data Classification' },
  { id: 'pattern-library',     label: 'Pattern Library' },
];

function PoliciesPageV2Inner() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'policy-skills');
  const onTabChange = (id) => {
    setTab(id);
    const params = new URLSearchParams(window.location.search);
    params.set('tab', id);
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  };

  return (
    <V2PageShell
      title="Policies"
      subtitle="8 tabs · policy authoring, framework coverage, data classification, pattern library"
      tags={<Pill tone="cyan" size="sm">Protect</Pill>}
      tabs={POLICY_TABS}
      activeTabId={tab}
      onTabChange={onTabChange}
      actions={<Button variant="primary">+ New policy</Button>}
    >
      {tab === 'policy-skills'       && <PolicySkillsTab navigate={navigate} />}
      {tab === 'policy-library'      && <PolicyLibraryTab navigate={navigate} />}
      {tab === 'control-config'      && <ControlConfigTab navigate={navigate} />}
      {tab === 'framework-coverage'  && <FrameworkCoverageTab navigate={navigate} />}
      {tab === 'control-drift'       && <ControlDriftTab navigate={navigate} />}
      {tab === 'control-scorecard'   && <ControlScorecardTab navigate={navigate} />}
      {tab === 'data-classification' && <DataClassificationTab navigate={navigate} />}
      {tab === 'pattern-library'     && <PatternLibraryTab navigate={navigate} />}
    </V2PageShell>
  );
}

function PolicySkillsTab({ navigate }) {
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-4 gap-4">
        <MetricCard label="Active policies" value="42" tone="cyan" delta={{ value: '+3', direction: 'up', baseline: 'this week', intent: 'positive' }} />
        <MetricCard label="Draft" value="6" tone="warning" />
        <MetricCard label="Paused" value="2" tone="muted" />
        <MetricCard label="Avg fire / 24h" value="284" tone="trust" />
      </div>
      <Card>
        <Card.Header title="Policy skills" actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/policy-config')}>Open in v1</Button>} />
        <Card.Body>
          <Table
            ariaLabel="Policy skills"
            density="compact"
            columns={[
              { key: 'name',      header: 'Policy', render: (r) => <strong className="text-v2-ink">{r.name}</strong> },
              { key: 'framework', header: 'Framework', render: (r) => <Pill tone="trust" size="sm">{r.framework}</Pill> },
              { key: 'state',     header: 'State', render: (r) => <Pill tone={r.state === 'active' ? 'success' : r.state === 'draft' ? 'warning' : 'muted'} dot size="sm">{r.state}</Pill> },
              { key: 'fires',     header: 'Fires / 24h', render: (r) => <span className="tabular-nums">{r.fires}</span> },
              { key: 'updated',   header: 'Updated', render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.updated}</span> },
            ]}
            rows={[
              { name: 'PHI-redaction-egress',  framework: 'HIPAA',     state: 'active', fires: 218, updated: 'May 7' },
              { name: 'PII-no-clear-text',     framework: 'GDPR',      state: 'active', fires: 412, updated: 'May 5' },
              { name: 'PCI-cardholder-flow',   framework: 'PCI DSS',   state: 'active', fires:  17, updated: 'May 6' },
              { name: 'SOC2-CC6-evidence',     framework: 'SOC 2',     state: 'active', fires:  88, updated: 'May 8' },
              { name: 'ISO27001-A18-classify', framework: 'ISO 27001', state: 'draft',  fires:   0, updated: 'May 9' },
            ]}
            rowKey={(r) => r.name}
          />
        </Card.Body>
      </Card>
    </div>
  );
}

function PolicyLibraryTab() {
  const ITEMS = [
    { name: 'PHIRedaction',           category: 'Data Privacy',      uses: 12 },
    { name: 'CardholderGuard',        category: 'Payment',           uses:  6 },
    { name: 'GDPRArticle30Logger',    category: 'GDPR',              uses:  9 },
    { name: 'PrivilegedActionGate',   category: 'Privileged Access', uses:  4 },
    { name: 'ToxicCombinationDetect', category: 'Risk',              uses:  3 },
    { name: 'EvidenceRetention90d',   category: 'Compliance',        uses: 24 },
  ];
  return (
    <Card>
      <Card.Header title="Reusable policy patterns" actions={<Pill tone="cyan" size="sm">{ITEMS.length} entries</Pill>} />
      <Card.Body>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {ITEMS.map(it => (
            <div key={it.name} className="border border-v2-border-subtle rounded-v2-md p-3 hover:border-v2-action transition-colors duration-v2">
              <div className="font-semibold text-v2-ink text-[13px]">{it.name}</div>
              <div className="text-[11px] text-v2-ink-muted mt-0.5">{it.category} · used by {it.uses} agentlets</div>
              <Button variant="ghost" size="sm" className="mt-2">Clone to new policy</Button>
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
}

function ControlConfigTab({ navigate }) {
  return (
    <Card>
      <Card.Header title="Per-control configuration" actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/policy-config?tab=control-config')}>Open in v1</Button>} />
      <Card.Body>
        <Table
          ariaLabel="Control config"
          density="compact"
          columns={[
            { key: 'control', header: 'Control', render: (r) => <code className="font-mono-v2 text-[11.5px] text-v2-ink">{r.control}</code> },
            { key: 'framework', header: 'Framework', render: (r) => <Pill tone="trust" size="sm">{r.framework}</Pill> },
            { key: 'event', header: 'Enforcement event', render: (r) => <span className="text-v2-ink-secondary">{r.event}</span> },
            { key: 'state', header: 'State', render: (r) => <Pill tone={r.state === 'mapped' ? 'success' : 'warning'} dot size="sm">{r.state}</Pill> },
          ]}
          rows={[
            { control: 'CC6.1', framework: 'SOC 2',     event: 'AccessGrant.assigned', state: 'mapped' },
            { control: 'CC6.2', framework: 'SOC 2',     event: 'AccessGrant.revoked',  state: 'mapped' },
            { control: '164.312(a)', framework: 'HIPAA', event: 'PHIAccess.audited',    state: 'mapped' },
            { control: 'A.18.1.4', framework: 'ISO 27001', event: 'PII.classified',     state: 'unmapped' },
          ]}
          rowKey={(r) => r.control}
        />
      </Card.Body>
    </Card>
  );
}

function FrameworkCoverageTab() {
  const FRAMES = [
    { name: 'SOC 2 Type II', covered: 64, total: 64, score: 100 },
    { name: 'HIPAA',         covered: 42, total: 48, score:  88 },
    { name: 'PCI DSS',       covered: 12, total: 12, score: 100 },
    { name: 'GDPR',          covered: 38, total: 42, score:  90 },
    { name: 'ISO 27001',     covered: 92, total: 114, score: 81 },
  ];
  return (
    <div className="space-y-5">
      <PostureBanner score={92} title="Coverage posture: Strong — 248 of 280 controls mapped" message="32 unmapped controls — 22 of those are ISO 27001 Annex A. Auto-renewal eligible after the next ISO bump." />
      <Card>
        <Card.Header title="Per-framework coverage" />
        <Card.Body>
          <Table
            ariaLabel="Framework coverage"
            density="compact"
            columns={[
              { key: 'name',     header: 'Framework', render: (r) => <strong className="text-v2-ink">{r.name}</strong> },
              { key: 'covered',  header: 'Covered',   render: (r) => <span className="tabular-nums">{r.covered} / {r.total}</span> },
              { key: 'score',    header: 'Score',     render: (r) => <span className={'tabular-nums font-semibold ' + (r.score < 90 ? 'text-v2-warning' : 'text-v2-success')}>{r.score}%</span> },
              { key: 'gap',      header: 'Gap',       render: (r) => <span className="text-v2-ink-muted">{r.total - r.covered} controls</span> },
            ]}
            rows={FRAMES}
            rowKey={(r) => r.name}
          />
        </Card.Body>
      </Card>
    </div>
  );
}

function ControlDriftTab() {
  return (
    <Card>
      <Card.Header title="Drift events · last 7 days" actions={<Pill tone="warning" dot size="sm">3 active</Pill>} />
      <Card.Body>
        <Table
          ariaLabel="Control drift"
          density="compact"
          columns={[
            { key: 'control', header: 'Control', render: (r) => <code className="font-mono-v2 text-[11px]">{r.control}</code> },
            { key: 'agent',   header: 'Agentlet', render: (r) => <strong className="text-v2-ink">{r.agent}</strong> },
            { key: 'metric',  header: 'Drift signal', render: (r) => <span>{r.metric}</span> },
            { key: 'delta',   header: 'Δ baseline', render: (r) => <span className="font-semibold text-v2-warning">{r.delta}</span> },
            { key: 'when',    header: 'When', align: 'right', render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.when}</span> },
          ]}
          rows={[
            { control: 'CC6.1',         agent: 'Sophie Cognito', metric: 'access_grant duration', delta: '+24%', when: 'May 8 13:08' },
            { control: '164.312(a)',    agent: 'Aria Threat',    metric: 'PHI access count',     delta: '+18%', when: 'May 7 22:11' },
            { control: 'PCI 3.4',       agent: 'Echo Compliance', metric: 'token vault uses',     delta: '+12%', when: 'May 6 18:04' },
          ]}
          rowKey={(r, i) => `${r.control}-${i}`}
        />
      </Card.Body>
    </Card>
  );
}

function ControlScorecardTab() {
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-3 gap-4">
        <MetricCard label="Avg score · 30d" value="92.4%" tone="success" delta={{ value: '+1.2pp', direction: 'up', intent: 'positive' }} trend={[88, 89, 90, 91, 92, 92, 92.4]} />
        <MetricCard label="Frameworks ≥ 95%" value="3 / 5" tone="cyan" />
        <MetricCard label="Lowest framework" value="ISO · 81%" tone="warning" />
      </div>
      <Card>
        <Card.Header title="Per-framework score history" />
        <Card.Body>
          <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
            Trend the last 30 days of compliance score per framework. Click any framework above to expand its daily score history with audit anchor points.
          </p>
        </Card.Body>
      </Card>
    </div>
  );
}

function DataClassificationTab() {
  return (
    <Card>
      <Card.Header title="DPG classification rules" actions={<Pill tone="cyan" size="sm">28 rules</Pill>} />
      <Card.Body>
        <Table
          ariaLabel="Data classification rules"
          density="compact"
          columns={[
            { key: 'name',    header: 'Rule', render: (r) => <strong className="text-v2-ink">{r.name}</strong> },
            { key: 'class',   header: 'Class', render: (r) => <Pill tone={r.class === 'PHI' ? 'danger' : r.class === 'PCI' ? 'warning' : 'cyan'} size="sm">{r.class}</Pill> },
            { key: 'pattern', header: 'Pattern', render: (r) => <code className="font-mono-v2 text-[11px] text-v2-ink-secondary">{r.pattern}</code> },
            { key: 'fires',   header: 'Fires / 24h', render: (r) => <span className="tabular-nums">{r.fires}</span> },
          ]}
          rows={[
            { name: 'SSN',                class: 'PII', pattern: '\\d{3}-\\d{2}-\\d{4}',                fires: 142 },
            { name: 'Credit card',        class: 'PCI', pattern: '^(?:\\d[ -]?){13,19}$',               fires:  17 },
            { name: 'Email address',      class: 'PII', pattern: '\\S+@\\S+\\.\\S+',                    fires: 412 },
            { name: 'US Diagnosis code',  class: 'PHI', pattern: '\\b[A-Z]\\d{2}(?:\\.\\d{1,2})?\\b',  fires:  42 },
            { name: 'AWS access key',     class: 'Secret', pattern: 'AKIA[0-9A-Z]{16}',                fires:   0 },
          ]}
          rowKey={(r) => r.name}
        />
      </Card.Body>
    </Card>
  );
}

function PatternLibraryTab() {
  const PATTERNS = [
    { name: 'Egress redact-or-block', uses: 18 },
    { name: 'Privileged access window', uses: 12 },
    { name: 'Toxic combo detect', uses:  8 },
    { name: 'Token vault rotate', uses:  6 },
    { name: 'Evidence pack on close', uses: 22 },
    { name: 'Drift baseline + alert', uses:  4 },
  ];
  return (
    <Card>
      <Card.Header title="Reusable enforcement patterns" />
      <Card.Body>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PATTERNS.map(p => (
            <div key={p.name} className="border border-v2-border-subtle rounded-v2-md p-3 hover:border-v2-action transition-colors duration-v2">
              <div className="font-semibold text-v2-ink text-[13px]">{p.name}</div>
              <div className="text-[11px] text-v2-ink-muted mt-0.5">used by {p.uses} policies</div>
              <Button variant="ghost" size="sm" className="mt-2">Instantiate</Button>
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
}

/* G116 — proper v2 inventory + tab pattern. */
import ResolvedPolicyConfigPageV2 from './PolicyConfigPageV2';
export default function ResolvedPoliciesPage(props) {
  return <ResolvedPolicyConfigPageV2 {...props} />;
}
export { PoliciesPageV2Inner };
